import { GetServerSidePropsContext, Page } from "next";
import { SiteContentScopes, PopupTags, TranslationScopes } from "@finbackoffice/enums";
import { useSiteConfig, useTranslation } from "@finbackoffice/site-core";
import { CasinoLayoutEnum, ISiteConfigLayouts } from "@finbackoffice/fe-core";
import { useMemo } from "react";
import { withSsrCasinoFavorites } from "@finbackoffice/site-server-core";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Seo from "components/base/seo/Seo";
import Casino from "components/pages/casino/Casino";
import {
    clientBFFSingleton,
    getServerData,
    createGetServerSideProps,
    CommonSsrProps,
    getServerRuntimeConfig,
} from "services/ssr";
import { getCurrentLocale } from "utils/helpers";
import { getSlotsLayout } from "components/layout/casino/slots";
import GameStore from "components/pages/casino/game-store/GameStore";

export const getServerSideProps = createGetServerSideProps<CommonSsrProps>(
    async (context: GetServerSidePropsContext) => {
        const runtimeConfig = await getServerRuntimeConfig();
        const client = clientBFFSingleton.getInstance(context.req.cookies.jwt);
        const locale = getCurrentLocale(runtimeConfig, context.locale);

        const [{ translations, configs, profile, popupBanners, banners }, casinoFavoriteGames] =
            await Promise.all([
                getServerData(runtimeConfig, context, client, {
                    popupBanners: { scope: [PopupTags.Slots], device: PopupTags.Desktop },
                    translationScope: [TranslationScopes.Casino, TranslationScopes.CasinoSlots],
                    banners: { scope: [SiteContentScopes.Casino, SiteContentScopes.Slots] },
                }),
                context.req.cookies.jwt ? withSsrCasinoFavorites(client, false) : undefined,
            ]);

        const props = {
            configs,
            runtimeConfig,
            jwt: context.req.cookies.jwt || "",
            profile: profile || null,
            locale,
            translations,
            banners: banners.items,
            popupBanners: popupBanners || [],
            casinoFavoriteGames: casinoFavoriteGames?.items || null,
        };

        return {
            props,
        };
    },
);

const SlotsPage: Page = () => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const { t } = useTranslation();

    const renderContent = useMemo(() => {
        switch (siteLayoutsConfig._casino.type) {
            case CasinoLayoutEnum.GameStore:
                return <GameStore />;
            default:
                return <Casino />;
        }
    }, [siteLayoutsConfig._casino.type]);

    return (
        <ErrorBoundary name={Casino.name}>
            <Seo
                page={TranslationScopes.CasinoSlots}
                replace={{
                    title: {
                        page_name: t(`pageName_slots`),
                    },
                }}
                twitterImg={`twitter-slots-social-image`}
                ogImg={`slots-social-image`}
            />
            {renderContent}
        </ErrorBoundary>
    );
};

SlotsPage.getLayout = getSlotsLayout;

export default SlotsPage;
